"use client";

import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRowActionItems, type RowActionItem } from "./use-row-action-items";
import { groupRowActionItems } from "./row-action-groups";
import { MORE_ACTIONS_LABEL } from "@/lib/actions/action-categories";
import type {
  DetailAction,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { EntityHooks, SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types/shared";

interface MobileRowActionsSheetProps<T extends BaseEntity> {
  item: T;
  actions: DetailAction<T>[];
  hooks: EntityHooks<T>;
  entityName: string;
  config?: SimpleTableConfig<T>;
  onClose: () => void;
}

/**
 * Mobile-friendly row actions rendered as a bottom-sheet body: full-width rows
 * with large (≥44px) tap targets instead of a small anchored dropdown. Uses the
 * same `useRowActionItems` source as the desktop dropdown, so behavior is
 * identical. Low-priority actions collapse behind a "More actions" disclosure;
 * destructive actions are pinned last.
 */
export function MobileRowActionsSheet<T extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
  onClose,
}: MobileRowActionsSheetProps<T>) {
  const { ready, partition } = useRowActionItems({ item, actions, hooks, entityName, config });
  const [showMore, setShowMore] = useState(false);

  if (!ready) {
    return (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
        No actions available.
      </p>
    );
  }

  const { top, more, danger } = partition;

  const renderRow = ({ action, label, isDisabled, isDestructive, run }: RowActionItem<T>) => (
    <Button
      key={action.id}
      type="button"
      variant="ghost"
      disabled={isDisabled}
      onClick={() => {
        run();
        onClose();
      }}
      className={cn(
        "h-12 w-full justify-start rounded-lg px-4 text-base font-normal",
        isDestructive &&
          "text-red-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
      )}
    >
      {label}
    </Button>
  );

  // Labeled category sections — same grouping as the right-click menu.
  // Destructive is pinned last and renders with no header.
  const renderRows = (rows: RowActionItem<T>[]) =>
    groupRowActionItems(rows).map((group, groupIndex) => (
      <React.Fragment key={group.category}>
        {groupIndex > 0 && <div className="my-1 border-t" />}
        {group.category !== "destructive" && (
          <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group.label}
          </p>
        )}
        {group.items.map(renderRow)}
      </React.Fragment>
    ));

  return (
    <div className="flex flex-col gap-1 p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
      {renderRows(top)}

      {more.length > 0 && (
        <>
          {top.length > 0 && <div className="my-1 border-t" />}
          {showMore ? (
            renderRows(more)
          ) : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowMore(true)}
              className="h-12 w-full justify-start rounded-lg px-4 text-base font-normal text-muted-foreground"
            >
              <ChevronDown className="mr-2 h-4 w-4" />
              {MORE_ACTIONS_LABEL}
            </Button>
          )}
        </>
      )}

      {danger.length > 0 && (
        <>
          {(top.length > 0 || more.length > 0) && <div className="my-1 border-t" />}
          {renderRows(danger)}
        </>
      )}
    </div>
  );
}
